import { Cancel, CheckCircle, ChevronRight } from '@mui/icons-material';
import { Box, IconButton, Tooltip } from '@mui/material';
import { t } from 'i18next';
import { ReactElement } from 'react';
import { FilterConfiguration } from '../../../common/components/FilterPanel/types';
import { TypographyOverflow } from '../../../common/components/TypographyOverflow';
import { PaginatedTableController, PaginatedTableState } from '../../../common/controllers/PaginatedTableController';
import { formatTlsVersion } from '../../../common/handshakes/enums/TlsVersion';
import { WithRouterProps, withRouter } from '../../../common/higher-order-components/with-router';
import { FieldConfiguration } from '../../../common/types/paginated-table';
import { BLANK_PAGINATION_RESULT, PaginationResult } from '../../../common/types/pagination';
import { bind } from '../../../common/utils/decorators';
import { stopPropagationHandler } from '../../../common/utils/handlers';
import { UrlHelper } from '../../../common/utils/UrlHelper';
import { PATHS } from '../../../routing/routes';
import { ReportOutletContextType } from '../../common/contexts/ReportOutletContext';
import { Report, ReportDone, ReportFailed, ReportPending } from '../../common/models/Report';
import { ReportCiphersuitesHeader } from '../components/ReportCiphersuitesHeader';
import { Ciphersuite } from '../models/Ciphersuite';
import { ReportCiphersuitesRepository } from '../services/ReportCiphersuitesRepository';

type Props = WithRouterProps<ReportOutletContextType> & {};

type State = PaginatedTableState & {
  isNegotiationFilterEnabled: boolean;
  isRecommendedFilterEnabled: boolean;
};

export class Controller extends PaginatedTableController<Ciphersuite, Props, State> {
  private static readonly FILTER_NEGOTIATION_KEY = 'negotiation';
  private static readonly FILTER_RECOMMENDED_KEY = 'recommended';

  private readonly reportCiphersuitesRepository = new ReportCiphersuitesRepository();
  private readonly reportId: number;

  constructor(props: Props, context: unknown) {
    super(props, context);

    const params = this.parseNumericIds(['reportId']);
    this.reportId = params.reportId;
  }

  componentDidMount(): void {
    super.componentDidMount();

    this.loadReportContext();
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>): void {
    super.componentDidUpdate(prevProps, prevState);

    this.onReportContextChanged(prevProps.context.report, this.props.context.report);
  }

  componentWillUnmount() {
    super.componentWillUnmount();

    this.reportCiphersuitesRepository.cancelAllRequests();
  }

  getBaseState(): State {
    const { negotiation, recommended } = this.extractQueryFilters();

    return {
      ...super.getBaseState(),
      isNegotiationFilterEnabled: negotiation,
      isRecommendedFilterEnabled: recommended,
    };
  }

  @bind
  onNegotiationFilterChange() {
    this.setState(
      (prev) => ({ ...prev, isNegotiationFilterEnabled: !prev.isNegotiationFilterEnabled }),
      () => {
        this.storeQueryFilters();
        this.reloadTableContent();
      }
    );
  }

  @bind
  onRecommendedFilterChange() {
    this.setState(
      (prev) => ({ ...prev, isRecommendedFilterEnabled: !prev.isRecommendedFilterEnabled }),
      () => {
        this.storeQueryFilters();
        this.reloadTableContent();
      }
    );
  }

  protected renderTable(tableComponent: ReactElement) {
    return (
      <>
        <ReportCiphersuitesHeader
          isToggleDisabled={this.state.loadingData}
          isNegotiationFilterEnabled={this.state.isNegotiationFilterEnabled}
          isRecommendedFilterEnabled={this.state.isRecommendedFilterEnabled}
          onNegotiationFilterChange={this.onNegotiationFilterChange}
          onRecommendedFilterChange={this.onRecommendedFilterChange}
        />
        {tableComponent}
      </>
    );
  }

  protected doInitialDataFetch() {
    // nothing, we aren't fetching anything until we receive a ReportDone from context
  }

  protected async fetchData(): Promise<PaginationResult<Ciphersuite>> {
    const { report } = this.props.context;

    if (!(report instanceof ReportDone) || report.getCiphersuitesCount() === 0) return BLANK_PAGINATION_RESULT;

    try {
      const { page, pageSize, isNegotiationFilterEnabled, isRecommendedFilterEnabled } = this.state;

      const ciphersuites = await this.reportCiphersuitesRepository.getList({
        reportId: report.getId(),
        onlyRecommended: isRecommendedFilterEnabled,
        onlyUsed: isNegotiationFilterEnabled,
        sorters: this.getSorters(),
      });

      const start = page * pageSize;
      const end = start + pageSize;

      return {
        data: ciphersuites.slice(start, end),
        totalCount: ciphersuites.length,
        pageInfo: {
          startCursor: null,
          endCursor: null,
          hasNextPage: end < ciphersuites.length,
          hasPreviousPage: start > 0,
        },
      };
    } catch (e: unknown) {
      this.handleError(e);
      return BLANK_PAGINATION_RESULT;
    }
  }

  protected getFieldConfigurations(): FieldConfiguration<Ciphersuite>[] {
    const getContentOrDefault = (value: string | null) => {
      return value?.replaceAll('_', '-') ?? t('ciphersuites.emptyCol');
    };

    return [
      {
        fieldKey: 'protocol',
        label: t('ciphersuites.protocol'),
        sortable: true,
        gqlKeys: { sorterField: 'name' },
        render: (item, { hasFocus }) => <TypographyOverflow hasFocus={hasFocus}>{getContentOrDefault(item.getProtocolName())}</TypographyOverflow>,
      },
      {
        fieldKey: 'version',
        label: t('ciphersuites.version'),
        sortable: true,
        gqlKeys: { sorterField: 'version' },
        render: (item, { hasFocus }) => <TypographyOverflow hasFocus={hasFocus}>{formatTlsVersion(item.getVersion())}</TypographyOverflow>,
      },
      {
        fieldKey: 'keyExchangeAlgorithm',
        label: t('ciphersuites.keyExchangeAlgorithm'),
        sortable: true,
        gqlKeys: { sorterField: 'kex' },
        render: (item, { hasFocus }) => <TypographyOverflow hasFocus={hasFocus}>{getContentOrDefault(item.getKeyExchangeAlgorithm())}</TypographyOverflow>,
      },
      {
        fieldKey: 'authenticationAlgorithm',
        label: t('ciphersuites.authenticationAlgorithm'),
        sortable: true,
        gqlKeys: { sorterField: 'auth' },
        render: (item, { hasFocus }) => <TypographyOverflow hasFocus={hasFocus}>{getContentOrDefault(item.getAuthenticationAlgorithm())}</TypographyOverflow>,
      },
      {
        fieldKey: 'symmetricAlgorithm',
        label: t('ciphersuites.symmetricAlgorithm'),
        sortable: true,
        gqlKeys: { sorterField: 'algorithmSym' },
        render: (item, { hasFocus }) => <TypographyOverflow hasFocus={hasFocus}>{getContentOrDefault(item.getSymmetricAlgorithm())}</TypographyOverflow>,
      },
      {
        fieldKey: 'macAlgorithm',
        label: t('ciphersuites.macAlgorithm'),
        sortable: true,
        gqlKeys: { sorterField: 'mac' },
        render: (item, { hasFocus }) => <TypographyOverflow hasFocus={hasFocus}>{getContentOrDefault(item.getMacAlgorithm())}</TypographyOverflow>,
      },
      {
        fieldKey: 'modeOfOperation',
        label: t('ciphersuites.modeOfOperation'),
        sortable: true,
        gqlKeys: { sorterField: 'modeOfOperation' },
        render: (item, { hasFocus }) => <TypographyOverflow hasFocus={hasFocus}>{getContentOrDefault(item.getModeOfOperation())}</TypographyOverflow>,
      },
      {
        fieldKey: 'hashAlgorithm',
        label: t('ciphersuites.hashAlgorithm'),
        sortable: true,
        gqlKeys: { sorterField: 'hashAlg' },
        render: (item, { hasFocus }) => <TypographyOverflow hasFocus={hasFocus}>{getContentOrDefault(item.getHashAlgorithm())}</TypographyOverflow>,
      },
      {
        fieldKey: 'recommended',
        label: t('ciphersuites.recommended'),
        sortable: true,
        gqlKeys: { sorterField: 'recommended' },
        render: (item) => (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {item.getRecommended() ? (
              <>
                <CheckCircle color="success" sx={{ mr: 0.5 }} />
                {t('yes')}
              </>
            ) : (
              <>
                <Cancel color="error" sx={{ mr: 0.5 }} />
                {t('no')}
              </>
            )}
          </Box>
        ),
      },
      {
        fieldKey: 'chevron',
        label: '',
        columnProps: { width: 52, maxWidth: 52, minWidth: 52, cellClassName: 'px-0', align: 'center' },
        render: (item, { tabIndex }) => {
          const label = this.state.isNegotiationFilterEnabled ? 'chevronSelectedTooltip' : 'chevronSupportedTooltip';
          return (
            <Tooltip title={t(`ciphersuites.${label}`)}>
              <IconButton
                tabIndex={tabIndex}
                // Prevent event propagation to `onItemClick`
                onClick={stopPropagationHandler}
                href={this.buildHandshakeFilterLink(item)}
                aria-label={t(`a11y.report.ciphersuites.${label}`, { ciphersuite: item.getName() ?? item.getId() })}
              >
                <ChevronRight />
              </IconButton>
            </Tooltip>
          );
        },
      },
    ];
  }

  protected onItemClick(item: Ciphersuite) {
    this.navigate(this.buildHandshakeFilterLink(item));
  }

  protected isItemClickable() {
    return true;
  }

  protected getFilterConfigurations(): FilterConfiguration[] {
    return [];
  }

  private extractQueryFilters() {
    const rawNegotiation = this.props.searchParams.get(Controller.FILTER_NEGOTIATION_KEY);
    const rawRecommendation = this.props.searchParams.get(Controller.FILTER_RECOMMENDED_KEY);

    // We don't want to validate anything besides matching the string "true".
    // If anyone changed the values, we default to false, as we would if the value was not passed in.

    return {
      negotiation: rawNegotiation === 'true',
      recommended: rawRecommendation === 'true',
    };
  }

  private buildHandshakeFilterLink(item: Ciphersuite): string {
    const handshakesPath = UrlHelper.generatePath(PATHS.REPORT_TLS_HANDSHAKES, { reportId: this.reportId.toString() });
    const queryParamName = this.state.isNegotiationFilterEnabled ? 'selected_ciphersuite' : 'supported_ciphersuite';
    const queryParams = `${queryParamName}=${item.getId()}`;

    return `${handshakesPath}?${queryParams}`;
  }

  private storeQueryFilters() {
    const { isNegotiationFilterEnabled, isRecommendedFilterEnabled } = this.state;
    this.props.setSearchParams(
      (prev) => {
        prev.delete(Controller.FILTER_NEGOTIATION_KEY);
        prev.delete(Controller.FILTER_RECOMMENDED_KEY);
        if (isNegotiationFilterEnabled) {
          prev.set(Controller.FILTER_NEGOTIATION_KEY, 'true');
        }
        if (isRecommendedFilterEnabled) {
          prev.set(Controller.FILTER_RECOMMENDED_KEY, 'true');
        }
        return prev;
      },
      { replace: true }
    );
  }

  private loadReportContext() {
    // When the component mounts, consider that the previous report is null
    this.onReportContextChanged(null, this.props.context.report);
  }

  private onReportContextChanged(prevReport: Report | null, currReport: Report | null) {
    // Report hasn't changed, do nothing
    if (prevReport === currReport) return;

    if (prevReport !== null && prevReport.getId() !== currReport?.getId()) {
      // Report has changed (e.g. through a version selector)
      // Reset the table state (including stored data and pagination) and reload
      this.resetState(() => this.loadReportContext());
      return;
    }

    if (!currReport || currReport instanceof ReportPending) {
      // The report is not ready yet, display the initial loading state
      this.resetState();
    } else if (currReport instanceof ReportFailed) {
      // The report is failed, display the blank loaded state
      this.setupBlankState();
    } else if (currReport instanceof ReportDone) {
      // The report is done, load the ciphersuites
      super.doInitialDataFetch();
    }
  }
}

export const ReportCiphersuitesController = withRouter<ReportOutletContextType>(Controller);
